import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { terminalSerial } from '@renderer/utils/pointPayment'

/**
 * Payments settings, `connected` mode: which card terminal this booth pushes
 * amounts to.
 *
 * The list comes from Studio (`GET /api/wallet/mercadopago/terminals`), which
 * reads it off the operator's active Mercado Pago wallet. The choice is stored
 * on this machine only (`cardTerminal.terminalId`), like the camera and the
 * printer, because the terminal stands next to THIS booth. A terminal takes
 * orders from the API only in PDV mode, and Mercado Pago applies a mode change
 * when the terminal restarts, so the switch says so.
 */

interface PointTerminal {
  id: string
  posId?: string
  storeId?: string
  externalPosId?: string
  operatingMode: 'PDV' | 'STANDALONE' | 'UNDEFINED'
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; terminals: PointTerminal[] }
  | { kind: 'not_applicable' }
  | { kind: 'error'; reason: string }

interface ConnectedTerminalPickerProps {
  primaryColor: string
  compact?: boolean
}

const TERMINALS_URL = `${process.env.VITE_API_URL}/api/wallet/mercadopago/terminals`

async function readErrorReason(response: Response): Promise<string> {
  const body = (await response.json().catch(() => null)) as {
    error?: string
    message?: string
  } | null
  return body?.error || body?.message || `HTTP ${response.status}`
}

export const ConnectedTerminalPicker: React.FC<ConnectedTerminalPickerProps> = ({
  primaryColor,
  compact = false
}) => {
  const { t } = useTranslation()
  const terminalId = useSettingsStore((state) => state.cardTerminal.terminalId)
  const setCardTerminal = useSettingsStore((state) => state.setCardTerminal)
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' })
  const [switchingId, setSwitchingId] = useState<string | null>(null)

  const loadTerminals = useCallback(async (): Promise<void> => {
    setLoadState({ kind: 'loading' })
    try {
      const response = await fetch(TERMINALS_URL, { credentials: 'include', cache: 'no-cache' })
      if (!response.ok) {
        setLoadState({ kind: 'error', reason: await readErrorReason(response) })
        return
      }
      const body = (await response.json()) as { status?: string; terminals?: PointTerminal[] }
      if (body.status === 'not_applicable') {
        setLoadState({ kind: 'not_applicable' })
        return
      }
      setLoadState({
        kind: 'ready',
        terminals: Array.isArray(body.terminals) ? body.terminals : []
      })
    } catch (error) {
      setLoadState({
        kind: 'error',
        reason: error instanceof Error ? error.message : String(error)
      })
    }
  }, [])

  useEffect(() => {
    void loadTerminals()
  }, [loadTerminals])

  const chooseTerminal = (id: string): void => {
    if (id === terminalId) return
    setCardTerminal({ terminalId: id })
    cogoToast.success(
      id
        ? t('settings.payments.terminalSaved', 'This booth now charges on that terminal')
        : t('settings.payments.terminalCleared', 'No terminal paired with this booth')
    )
  }

  const switchToPdv = async (id: string): Promise<void> => {
    setSwitchingId(id)
    try {
      const response = await fetch(TERMINALS_URL, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ terminalId: id, operatingMode: 'PDV' })
      })
      if (!response.ok) {
        cogoToast.error(
          t('settings.payments.terminalPdvFailed', 'Could not switch the terminal: {{reason}}', {
            reason: await readErrorReason(response)
          })
        )
        return
      }
      cogoToast.success(
        t(
          'settings.payments.terminalPdvDone',
          'Switched to PDV mode. Restart the terminal to finish.'
        )
      )
      await loadTerminals()
    } catch (error) {
      cogoToast.error(
        t('settings.payments.terminalPdvFailed', 'Could not switch the terminal: {{reason}}', {
          reason: error instanceof Error ? error.message : String(error)
        })
      )
    } finally {
      setSwitchingId(null)
    }
  }

  const textClass = compact ? 'text-xs' : 'text-sm'
  const hintClass = compact ? 'text-[11px]' : 'text-xs'
  const terminals = loadState.kind === 'ready' ? loadState.terminals : []
  const savedTerminalListed = Boolean(
    terminalId && terminals.some((terminal) => terminal.id === terminalId)
  )
  // Only claim a saved terminal is missing once there is an answer to compare with.
  const showSavedMissing =
    Boolean(terminalId) &&
    !savedTerminalListed &&
    (loadState.kind === 'ready' || loadState.kind === 'not_applicable')

  const choiceRowClass = (selected: boolean): string =>
    `flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors ${
      selected ? '' : 'border-gray-200 bg-white hover:bg-gray-50'
    }`
  const selectedStyle = (selected: boolean): React.CSSProperties | undefined =>
    selected ? { borderColor: primaryColor, backgroundColor: `${primaryColor}14` } : undefined

  return (
    <div className="space-y-3">
      {loadState.kind === 'loading' && (
        <p role="status" className={`text-gray-500 ${textClass}`}>
          {t('settings.payments.terminalLoading', 'Looking for terminals...')}
        </p>
      )}

      {loadState.kind === 'not_applicable' && (
        <p
          className={`rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700 ${textClass}`}
        >
          {t(
            'settings.payments.terminalNotApplicable',
            'Your active payment gateway is not Mercado Pago. Make your Mercado Pago wallet active in the dashboard to use a connected terminal.'
          )}
        </p>
      )}

      {loadState.kind === 'error' && (
        <div
          role="alert"
          className={`flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700 ${textClass}`}
        >
          <span className="min-w-0 flex-1 break-words">
            {t('settings.payments.terminalError', 'Could not load terminals: {{reason}}', {
              reason: loadState.reason
            })}
          </span>
          <button
            type="button"
            onClick={() => void loadTerminals()}
            className="min-h-[44px] rounded-md px-3 font-medium underline underline-offset-2"
          >
            {t('settings.payments.terminalRetry', 'Try again')}
          </button>
        </div>
      )}

      {loadState.kind === 'ready' && (
        <div
          role="radiogroup"
          aria-label={t('settings.payments.terminalTitle', 'Terminal')}
          className="space-y-2"
        >
          {terminals.length === 0 && (
            <p className={`text-gray-500 ${textClass}`}>
              {t(
                'settings.payments.terminalNone',
                'No Point terminals found on your Mercado Pago account.'
              )}
            </p>
          )}

          {terminals.map((terminal) => {
            const selected = terminal.id === terminalId
            const isPdv = terminal.operatingMode === 'PDV'
            return (
              <div key={terminal.id} className="flex flex-wrap items-stretch gap-2">
                <label className={choiceRowClass(selected)} style={selectedStyle(selected)}>
                  <input
                    type="radio"
                    name="connected-terminal"
                    checked={selected}
                    onChange={() => chooseTerminal(terminal.id)}
                    className="h-4 w-4 shrink-0"
                    style={{ accentColor: primaryColor }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate font-medium text-gray-900 ${textClass}`}>
                      {t('settings.payments.terminalSerial', 'Serial {{serial}}', {
                        serial: terminalSerial(terminal.id)
                      })}
                    </span>
                    <span
                      className={`block ${hintClass} ${isPdv ? 'text-green-700' : 'text-amber-700'}`}
                    >
                      {isPdv
                        ? t('settings.payments.terminalReady', 'Ready (PDV mode)')
                        : t('settings.payments.terminalNotPdv', 'Not in PDV mode yet')}
                    </span>
                  </span>
                </label>
                {!isPdv && (
                  <button
                    type="button"
                    onClick={() => void switchToPdv(terminal.id)}
                    disabled={switchingId !== null}
                    className={`min-h-[44px] rounded-md border border-gray-300 bg-white px-3 font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 ${textClass}`}
                  >
                    {switchingId === terminal.id
                      ? t('settings.payments.terminalSwitching', 'Switching...')
                      : t('settings.payments.terminalSwitchToPdv', 'Switch to PDV mode')}
                  </button>
                )}
              </div>
            )
          })}

          <button
            type="button"
            onClick={() => void loadTerminals()}
            className={`min-h-[44px] rounded-md px-1 font-medium text-blue-600 underline underline-offset-2 ${textClass}`}
          >
            {t('settings.payments.terminalRefresh', 'Refresh list')}
          </button>
        </div>
      )}

      {showSavedMissing && terminalId && (
        <div
          className={`flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 ${textClass}`}
        >
          <span className="min-w-0 flex-1 break-words">
            {t(
              'settings.payments.terminalMissing',
              'The saved terminal ({{serial}}) is not on this Mercado Pago account.',
              { serial: terminalSerial(terminalId) }
            )}
          </span>
          <button
            type="button"
            onClick={() => chooseTerminal('')}
            className="min-h-[44px] rounded-md px-3 font-medium underline underline-offset-2"
          >
            {t('settings.payments.terminalClear', 'Stop using it')}
          </button>
        </div>
      )}

      {!terminalId && loadState.kind === 'ready' && (
        <p
          className={`rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 ${textClass}`}
        >
          {t(
            'settings.payments.terminalNotPicked',
            'Pick a terminal. Until then the booth shows no card button.'
          )}
        </p>
      )}

      <p className={`text-gray-500 ${hintClass}`}>
        {t(
          'settings.payments.terminalRestartHint',
          'A terminal takes payments from the booth only in PDV mode. After switching, restart the terminal and check that its linking mode shows Punto de Venta (PDV).'
        )}
      </p>
    </div>
  )
}

export default ConnectedTerminalPicker
